import { importsApis } from '@gleamer/apis';
import { Imports } from '@gleamer/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import {
  IMPORT_BATCHES_KEY,
  useAuthorization,
  useImportSources,
} from '../../../../hooks/useImports';
import { PageButton } from '../../../atoms/PageButton';
import {
  Card,
  Field,
  FilePicker,
  inputClassName,
  selectClassName,
} from '../../../atoms/Surface';

const { InvalidListError, submitImportList } = importsApis;

type ImportSubmitted = Imports.ImportSubmitted;
type InvalidListLine = Imports.InvalidListLine;

/** viewer-api refuses a list above this size; checked here first so a wrong file fails at once. */
export const MAX_LIST_BYTES = 1024 * 1024;

type Outcome =
  | { kind: 'submitted'; submitted: ImportSubmitted }
  | { kind: 'refused'; message: string; invalidLines: InvalidListLine[] }
  | { kind: 'failed'; message: string };

/**
 * Submits a data manager's import list — one `CLIENT/PATIENT` per line, read from a source bucket — as
 * one import batch. viewer-api checks the whole list first: a list with a line at fault imports
 * nothing, and says which lines to fix. The batch then appears in the list below, followed line by line.
 */
export function SubmitImportList() {
  const auth = useAuthorization();
  const queryClient = useQueryClient();
  const sources = useImportSources();
  const [file, setFile] = useState<File | null>(null);
  const [bucket, setBucket] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const buckets = sources.data?.buckets ?? [];
  useEffect(() => {
    if (!bucket && buckets.length > 0) {
      setBucket(buckets[0]);
    }
  }, [bucket, buckets]);

  const tooLarge = !!file && file.size > MAX_LIST_BYTES;
  const canSubmit = !!file && !tooLarge && !!bucket && !submitting;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setOutcome(null);
    try {
      const submitted = await submitImportList(auth, {
        file,
        fileName: file.name,
        bucket,
        name: name.trim() || undefined,
      });
      setOutcome({ kind: 'submitted', submitted });
      setFile(null);
      setName('');
      if (fileInput.current) {
        fileInput.current.value = '';
      }
      queryClient.invalidateQueries({ queryKey: [IMPORT_BATCHES_KEY] });
    } catch (error) {
      if (error instanceof InvalidListError) {
        setOutcome({
          kind: 'refused',
          message: error.message,
          invalidLines: error.invalidLines,
        });
      } else {
        setOutcome({
          kind: 'failed',
          message: (error as Error)?.message ?? String(error),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      id="submit-list-title"
      title="Import a list"
      className="mb-6"
      description={
        <>
          A text file with one <span className="font-mono">CLIENT/PATIENT</span> per line,
          pointing into the source bucket. Every file under each line is imported, then exported
          to BigQuery.
        </>
      }
    >
      {sources.status === 'error' && (
        <p role="alert" className="mb-2 text-dl-danger">
          Could not load the source buckets: {(sources.error as Error)?.message}
        </p>
      )}
      <form onSubmit={submit} className="flex flex-wrap items-end gap-x-6 gap-y-4">
        <Field label="List file" hint={`.txt or .csv, at most ${MAX_LIST_BYTES / 1024} KiB`}>
          <FilePicker
            inputRef={fileInput}
            file={file}
            label="Choose a list…"
            aria-label="List file"
            accept=".txt,.csv,text/plain"
            onChange={e => {
              setFile(e.target.files?.[0] ?? null);
              setOutcome(null);
            }}
          />
        </Field>
        <Field label="Source bucket" hint={'\u00a0'}>
          <select
            className={selectClassName}
            value={bucket}
            onChange={e => setBucket(e.target.value)}
            disabled={buckets.length === 0}
          >
            {buckets.map(b => (
              <option key={b} value={b}>
                gs://{b}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name (optional)" hint="Shown in the list of batches">
          <input
            className={inputClassName}
            type="text"
            value={name}
            placeholder={file?.name ?? 'the file name'}
            onChange={e => setName(e.target.value)}
          />
        </Field>
        <div className="flex flex-col gap-1">
          <PageButton
            type="submit"
            tone="primary"
            disabled={!canSubmit}
            className="mx-0 h-10 px-5 text-base font-medium"
          >
            {submitting ? 'Submitting…' : 'Import the list'}
          </PageButton>
          <span className="text-sm text-dl-faint">
            {!file ? 'Choose a list first' : '\u00a0'}
          </span>
        </div>
      </form>
      {tooLarge && (
        <p role="alert" className="mt-2 text-dl-danger">
          The list is larger than {MAX_LIST_BYTES / 1024} KiB: split it.
        </p>
      )}
      {outcome?.kind === 'submitted' && (
        <p
          role="status"
          className="mt-2 rounded-md border border-dl-success/40 bg-dl-success-tint p-2"
        >
          <span className="font-bold">{outcome.submitted.name}</span> submitted:{' '}
          {outcome.submitted.distinct} patients from {outcome.submitted.lines}{' '}
          lines
          {outcome.submitted.duplicates > 0 &&
            ` (${outcome.submitted.duplicates} repeated)`}
          . It appears in the list below within a few seconds.
        </p>
      )}
      {outcome?.kind === 'refused' && (
        <div
          role="alert"
          className="mt-2 rounded-md border border-dl-danger/40 bg-dl-danger-tint p-2 text-dl-danger"
        >
          <p className="font-bold">
            The list was refused, nothing is imported: {outcome.message}
          </p>
          <ul className="mt-1 font-mono text-sm">
            {outcome.invalidLines.map(l => (
              <li key={l.line}>
                line {l.line}: {l.text === '' ? '(empty)' : l.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      {outcome?.kind === 'failed' && (
        <p
          role="alert"
          className="mt-2 rounded-md border border-dl-danger/40 bg-dl-danger-tint p-2 text-dl-danger"
        >
          The list could not be submitted: {outcome.message}
        </p>
      )}
    </Card>
  );
}
